/**
 * Comprehensive Three.js Section Visualizers Engine
 * 
 * Distinct, thematic 3D WebGL visualizers for each key section of the website.
 * Each visualizer runs ONLY when its section is in the viewport via IntersectionObserver,
 * pausing rendering immediately upon leaving the viewport to preserve 100% performance.
 */
(function() {
  'use strict';

  // Global helper to create a managed WebGL context with true lazy initialization via IntersectionObserver
  function createManagedVisualizer(canvasId, containerId, initSceneCallback, immediate) {
    const canvas = document.getElementById(canvasId);
    const container = document.getElementById(containerId);

    if (!canvas || !container || typeof THREE === 'undefined') {
      return null;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768;

    let renderer = null;
    let scene = null;
    let camera = null;
    let visualizerInstance = null;
    let isInitialized = false;
    let isVisible = false;
    let animationFrameId = null;

    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;

    function updateDimensions() {
      if (!renderer || !camera || !container) return;
      const width = container.offsetWidth || window.innerWidth;
      const height = container.offsetHeight || container.clientHeight || 600;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    }

    function initWebGL() {
      if (isInitialized) return;
      isInitialized = true;

      try {
        renderer = new THREE.WebGLRenderer({
          canvas: canvas,
          alpha: true,
          antialias: !isMobile,
          powerPreference: 'low-power'
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.25 : 1.75));

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(45, 16 / 9, 0.1, 1000);
        camera.position.z = 60;

        updateDimensions();

        visualizerInstance = initSceneCallback(scene, camera, isMobile, prefersReducedMotion);

        if (prefersReducedMotion && visualizerInstance && renderer) {
          renderer.render(scene, camera);
        }
      } catch (e) {
        // Graceful fallback if WebGL limit reached
      }
    }

    if (!prefersReducedMotion && !isMobile) {
      window.addEventListener('mousemove', function(e) {
        if (!isVisible) return;
        const halfWidth = window.innerWidth / 2;
        const halfHeight = window.innerHeight / 2;
        mouseX = (e.clientX - halfWidth) / halfWidth;
        mouseY = (e.clientY - halfHeight) / halfHeight;
      }, { passive: true });
    }

    function renderLoop(time) {
      if (!isVisible || prefersReducedMotion || !renderer || !scene || !camera) {
        animationFrameId = null;
        return;
      }

      animationFrameId = requestAnimationFrame(renderLoop);

      targetX += (mouseX * 0.1 - targetX) * 0.04;
      targetY += (mouseY * 0.1 - targetY) * 0.04;

      if (visualizerInstance && typeof visualizerInstance.update === 'function') {
        visualizerInstance.update(time || performance.now(), targetX, targetY);
      }

      renderer.render(scene, camera);
    }

    function startRendering() {
      if (!isInitialized) {
        initWebGL();
      }
      isVisible = true;
      if (!prefersReducedMotion && !animationFrameId && renderer) {
        updateDimensions();
        animationFrameId = requestAnimationFrame(renderLoop);
      }
    }

    function stopRendering() {
      isVisible = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    }

    // IntersectionObserver to ONLY initialize and render when scrolled into view
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            startRendering();
          } else {
            stopRendering();
          }
        });
      }, { rootMargin: '100px 0px', threshold: 0.02 });
      observer.observe(container);
    } else {
      startRendering();
    }

    // Debounced Resize Listener
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function() {
        if (isInitialized && isVisible) {
          updateDimensions();
        }
      }, 150);
    }, { passive: true });

    // If hero (immediate = true), initialize immediately
    if (immediate) {
      startRendering();
    }

    return {
      destroy: function() {
        stopRendering();
        if (renderer) {
          renderer.dispose();
        }
      }
    };
  }

  /* ==========================================================================
     1. HERO: WebXR VR Sandbox Floating Architectural Modules
     ========================================================================== */
  function initHeroVisualizer() {
    createManagedVisualizer('hero-three-canvas', 'hero-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 10, isMobile ? 85 : 65);
      camera.lookAt(0, 0, 0);

      const ambient = new THREE.AmbientLight(0x0b1120, 1.2);
      scene.add(ambient);
      const light1 = new THREE.DirectionalLight(0x38bdf8, 1.4);
      light1.position.set(30, 40, 20);
      scene.add(light1);
      const light2 = new THREE.DirectionalLight(0x6366f1, 1.0);
      light2.position.set(-30, -20, -10);
      scene.add(light2);

      const group = new THREE.Group();
      scene.add(group);

      const gridHelper = new THREE.GridHelper(isMobile ? 80 : 120, isMobile ? 16 : 24, 0x2563eb, 0x1e293b);
      gridHelper.position.y = -18;
      gridHelper.material.opacity = 0.35;
      gridHelper.material.transparent = true;
      group.add(gridHelper);

      const matCyan = new THREE.MeshStandardMaterial({ color: 0x38bdf8, roughness: 0.25, metalness: 0.8, transparent: true, opacity: 0.68 });
      const matBlue = new THREE.MeshStandardMaterial({ color: 0x2563eb, roughness: 0.3, metalness: 0.7, transparent: true, opacity: 0.65 });
      const matIndigo = new THREE.MeshStandardMaterial({ color: 0x6366f1, roughness: 0.2, metalness: 0.85, transparent: true, opacity: 0.68 });
      const matWire = new THREE.MeshBasicMaterial({ color: 0x38bdf8, wireframe: true, transparent: true, opacity: 0.35 });

      const objects = [];

      const torusKnot = new THREE.Mesh(new THREE.TorusKnotGeometry(isMobile ? 3.5 : 4.8, 0.9, isMobile ? 48 : 80, 16), matIndigo);
      torusKnot.position.set(isMobile ? 0 : 22, isMobile ? 8 : 4, -10);
      group.add(torusKnot);
      objects.push({ mesh: torusKnot, rx: 0.005, ry: 0.008, rz: 0.003, floatSpeed: 0.0015, floatOffset: 0 });

      const icosa = new THREE.Mesh(new THREE.IcosahedronGeometry(isMobile ? 3.2 : 4.2, 0), matCyan);
      icosa.position.set(isMobile ? -14 : -24, isMobile ? -6 : 6, -5);
      icosa.add(new THREE.Mesh(new THREE.IcosahedronGeometry(isMobile ? 3.2 : 4.2, 0), matWire));
      group.add(icosa);
      objects.push({ mesh: icosa, rx: 0.006, ry: -0.007, rz: 0.004, floatSpeed: 0.0018, floatOffset: 1.5 });

      const octa = new THREE.Mesh(new THREE.OctahedronGeometry(isMobile ? 2.8 : 3.6, 0), matBlue);
      octa.position.set(isMobile ? 14 : -10, isMobile ? -8 : -8, 5);
      group.add(octa);
      objects.push({ mesh: octa, rx: -0.007, ry: 0.005, rz: -0.004, floatSpeed: 0.002, floatOffset: 3 });

      if (!isMobile) {
        const box = new THREE.Mesh(new THREE.BoxGeometry(4.5, 4.5, 4.5), matBlue);
        box.position.set(16, -10, 0);
        box.add(new THREE.Mesh(new THREE.BoxGeometry(4.5, 4.5, 4.5), matWire));
        group.add(box);
        objects.push({ mesh: box, rx: 0.004, ry: 0.006, rz: 0.002, floatSpeed: 0.0012, floatOffset: 4.5 });

        const ring = new THREE.Mesh(new THREE.TorusGeometry(3.8, 0.4, 16, 48), matCyan);
        ring.position.set(0, 14, -15);
        group.add(ring);
        objects.push({ mesh: ring, rx: 0.008, ry: 0.003, rz: 0.006, floatSpeed: 0.0014, floatOffset: 2.2 });
      }

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX;
          group.rotation.x = -targetY * 0.8;
          if (!prefersReducedMotion) {
            for (let i = 0; i < objects.length; i++) {
              const item = objects[i];
              item.mesh.rotation.x += item.rx;
              item.mesh.rotation.y += item.ry;
              item.mesh.rotation.z += item.rz;
              item.mesh.position.y += Math.sin(time * item.floatSpeed + item.floatOffset) * 0.03;
            }
          }
        }
      };
    }, true);
  }

  /* ==========================================================================
     2. SERVICES: Rotating Neural Helix & Polyhedral Service Matrix
     ========================================================================== */
  function initServicesVisualizer() {
    createManagedVisualizer('services-three-canvas', 'services-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 0, 55);

      const light = new THREE.DirectionalLight(0x38bdf8, 1.2);
      light.position.set(20, 20, 20);
      scene.add(light);

      const group = new THREE.Group();
      scene.add(group);

      // Double Helix Particles
      const helixCount = isMobile ? 40 : 80;
      const helixGeo = new THREE.BufferGeometry();
      const positions = new Float32Array(helixCount * 2 * 3);

      for (let i = 0; i < helixCount; i++) {
        const theta = (i / helixCount) * Math.PI * 4;
        const y = (i / helixCount - 0.5) * 50;
        const radius = isMobile ? 12 : 18;

        // Strand 1
        positions[i * 6] = Math.cos(theta) * radius;
        positions[i * 6 + 1] = y;
        positions[i * 6 + 2] = Math.sin(theta) * radius;

        // Strand 2
        positions[i * 6 + 3] = Math.cos(theta + Math.PI) * radius;
        positions[i * 6 + 4] = y;
        positions[i * 6 + 5] = Math.sin(theta + Math.PI) * radius;
      }

      helixGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const helixMat = new THREE.PointsMaterial({ color: 0x38bdf8, size: 2.2, transparent: true, opacity: 0.5 });
      const helixPoints = new THREE.Points(helixGeo, helixMat);
      group.add(helixPoints);

      // Orbiting Service Polyhedrons
      const nodeMat = new THREE.MeshStandardMaterial({ color: 0x2563eb, roughness: 0.3, metalness: 0.8, wireframe: true });
      const nodes = [];
      for (let j = 0; j < (isMobile ? 3 : 5); j++) {
        const node = new THREE.Mesh(new THREE.OctahedronGeometry(2.2, 0), nodeMat);
        group.add(node);
        nodes.push({ mesh: node, angle: (j / 5) * Math.PI * 2, speed: 0.008 + j * 0.002, dist: 16 + j * 2 });
      }

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 1.2 + (prefersReducedMotion ? 0 : time * 0.0004);
          group.rotation.x = -targetY * 0.6;

          if (!prefersReducedMotion) {
            for (let k = 0; k < nodes.length; k++) {
              const n = nodes[k];
              n.angle += n.speed * 0.2;
              n.mesh.position.x = Math.cos(n.angle) * n.dist;
              n.mesh.position.z = Math.sin(n.angle) * n.dist;
              n.mesh.position.y = Math.sin(time * 0.002 + k) * 4;
              n.mesh.rotation.x += 0.01;
              n.mesh.rotation.y += 0.015;
            }
          }
        }
      };
    });
  }

  /* ==========================================================================
     3. STAGES / METHODOLOGY: Dynamic Undulating Pipeline Wave Grid
     ========================================================================== */
  function initStagesVisualizer() {
    createManagedVisualizer('stages-three-canvas', 'stages-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 18, 50);
      camera.lookAt(0, 0, 0);

      const light = new THREE.DirectionalLight(0x06b6d4, 1.2);
      light.position.set(0, 30, 20);
      scene.add(light);

      const group = new THREE.Group();
      scene.add(group);

      // Plane Wave Geometry
      const segsX = isMobile ? 18 : 32;
      const segsY = isMobile ? 14 : 24;
      const planeGeo = new THREE.PlaneGeometry(80, 50, segsX, segsY);
      planeGeo.rotateX(-Math.PI / 2.3);

      const planeMat = new THREE.MeshBasicMaterial({ color: 0x6366f1, wireframe: true, transparent: true, opacity: 0.35 });
      const waveMesh = new THREE.Mesh(planeGeo, planeMat);
      waveMesh.position.y = -8;
      group.add(waveMesh);

      // 3 Hovering Triangular Prisms representing Build, Develop, Optimize
      const prismMat = new THREE.MeshStandardMaterial({ color: 0x38bdf8, roughness: 0.2, metalness: 0.9, transparent: true, opacity: 0.7 });
      const prisms = [];
      const prismX = [-18, 0, 18];

      for (let p = 0; p < 3; p++) {
        const prism = new THREE.Mesh(new THREE.ConeGeometry(2.5, 4.5, 3), prismMat);
        prism.position.set(prismX[p], 2, 0);
        group.add(prism);
        prisms.push(prism);
      }

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 0.8;
          group.rotation.x = -targetY * 0.4;

          if (!prefersReducedMotion) {
            const pos = planeGeo.attributes.position.array;
            for (let i = 0; i < pos.length; i += 3) {
              const u = pos[i];
              const v = pos[i + 1];
              pos[i + 2] = Math.sin(u * 0.15 + time * 0.002) * Math.cos(v * 0.15 + time * 0.002) * 2.5;
            }
            planeGeo.attributes.position.needsUpdate = true;

            for (let j = 0; j < prisms.length; j++) {
              prisms[j].rotation.y += 0.01;
              prisms[j].position.y = 2 + Math.sin(time * 0.002 + j * 1.5) * 1.2;
            }
          }
        }
      };
    });
  }

  /* ==========================================================================
     4. WORK: Orbital Geospatial Rings & Locked-Track Satellites
     ========================================================================== */
  function initWorkVisualizer() {
    createManagedVisualizer('work-three-canvas', 'work-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 0, 50);

      const light = new THREE.DirectionalLight(0x38bdf8, 1.4);
      light.position.set(10, 20, 20);
      scene.add(light);

      const ambient = new THREE.AmbientLight(0x0b1120, 1.0);
      scene.add(ambient);

      const group = new THREE.Group();
      scene.add(group);

      const rings = [];
      const ringRadii = [15, 21, 27];
      const ringColors = [0x38bdf8, 0x2563eb, 0x6366f1];

      for (let r = 0; r < (isMobile ? 2 : 3); r++) {
        const radius = ringRadii[r];
        const ringGeo = new THREE.TorusGeometry(radius, 0.14, 12, isMobile ? 48 : 80);
        const ringMat = new THREE.MeshBasicMaterial({
          color: ringColors[r],
          transparent: true,
          opacity: 0.38
        });
        const ringMesh = new THREE.Mesh(ringGeo, ringMat);

        // Fixed 3D orientation tilt for the orbital plane
        ringMesh.rotation.x = 0.8 + r * 0.4;
        ringMesh.rotation.y = 0.5 - r * 0.3;
        group.add(ringMesh);

        // Create glowing satellite sphere attached directly to the ring mesh
        const satGeo = new THREE.SphereGeometry(isMobile ? 0.7 : 0.9, 16, 16);
        const satMat = new THREE.MeshBasicMaterial({
          color: r === 0 ? 0x38bdf8 : (r === 1 ? 0x60a5fa : 0xa5b4fc)
        });
        const satMesh = new THREE.Mesh(satGeo, satMat);

        // Add a subtle glow halo around the sphere
        const glowGeo = new THREE.SphereGeometry(isMobile ? 1.2 : 1.5, 12, 12);
        const glowMat = new THREE.MeshBasicMaterial({
          color: ringColors[r],
          transparent: true,
          opacity: 0.35,
          blending: THREE.AdditiveBlending
        });
        satMesh.add(new THREE.Mesh(glowGeo, glowMat));

        // Add to ringMesh so it stays in the ring's exact local 3D plane
        ringMesh.add(satMesh);

        rings.push({
          mesh: ringMesh,
          sat: satMesh,
          radius: radius,
          angle: r * 2.0,
          speed: (0.012 / (r + 1)) * 1.5
        });
      }

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 0.7;
          group.rotation.x = -targetY * 0.4;

          if (!prefersReducedMotion) {
            for (let i = 0; i < rings.length; i++) {
              const item = rings[i];
              item.angle += item.speed;
              
              // Exactly follows the circular perimeter of the Torus in local coordinates
              item.sat.position.x = Math.cos(item.angle) * item.radius;
              item.sat.position.y = Math.sin(item.angle) * item.radius;
              item.sat.position.z = 0;
            }
          }
        }
      };
    });
  }

  /* ==========================================================================
     5. TECHNOLOGY: Floating Layered Architectural Slabs
     ========================================================================== */
  function initTechVisualizer() {
    createManagedVisualizer('tech-three-canvas', 'tech-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 4, 45);
      camera.lookAt(0, 0, 0);

      const light = new THREE.DirectionalLight(0x38bdf8, 1.2);
      light.position.set(20, 30, 20);
      scene.add(light);

      const group = new THREE.Group();
      scene.add(group);

      const slabs = [];
      const slabCount = isMobile ? 3 : 5;
      const slabColors = [0x2563eb, 0x38bdf8, 0x6366f1, 0x06b6d4, 0x1e293b];

      for (let s = 0; s < slabCount; s++) {
        const slabGeo = new THREE.BoxGeometry(22 - s * 2, 0.6, 14 - s);
        const slabMat = new THREE.MeshStandardMaterial({
          color: slabColors[s % slabColors.length],
          roughness: 0.3,
          metalness: 0.8,
          transparent: true,
          opacity: 0.5
        });
        const slab = new THREE.Mesh(slabGeo, slabMat);
        slab.position.y = (s - slabCount / 2) * 4.5;
        group.add(slab);
        slabs.push({ mesh: slab, baseHeight: slab.position.y, phase: s * 0.8 });
      }

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 1.0;
          group.rotation.x = -targetY * 0.5 + 0.2;

          if (!prefersReducedMotion) {
            for (let i = 0; i < slabs.length; i++) {
              slabs[i].mesh.position.y = slabs[i].baseHeight + Math.sin(time * 0.0018 + slabs[i].phase) * 0.8;
              slabs[i].mesh.rotation.y = Math.sin(time * 0.001 + i) * 0.08;
            }
          }
        }
      };
    });
  }

  /* ==========================================================================
     6. PERFORMANCE: Geodesic Telemetry Radar & Pulse Waves
     ========================================================================== */
  function initPerfVisualizer() {
    createManagedVisualizer('perf-three-canvas', 'perf-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 0, 45);

      const light = new THREE.DirectionalLight(0x10b981, 1.2);
      light.position.set(10, 20, 20);
      scene.add(light);

      const group = new THREE.Group();
      scene.add(group);

      // Geodesic Wireframe Core
      const geoGeo = new THREE.IcosahedronGeometry(isMobile ? 8 : 12, 1);
      const geoMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, wireframe: true, transparent: true, opacity: 0.4 });
      const sphere = new THREE.Mesh(geoGeo, geoMat);
      group.add(sphere);

      // Radar Pulse Rings
      const ringGeo = new THREE.RingGeometry(isMobile ? 10 : 14, isMobile ? 10.4 : 14.5, 32);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0x10b981, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });
      const ring1 = new THREE.Mesh(ringGeo, ringMat);
      ring1.rotation.x = Math.PI / 2;
      group.add(ring1);

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 0.8 + (prefersReducedMotion ? 0 : time * 0.0006);
          group.rotation.x = -targetY * 0.6;

          if (!prefersReducedMotion) {
            sphere.rotation.y += 0.003;
            sphere.rotation.x += 0.002;
            const scale = 1 + Math.sin(time * 0.003) * 0.06;
            sphere.scale.set(scale, scale, scale);
            ring1.rotation.z += 0.01;
          }
        }
      };
    });
  }

  /* ==========================================================================
     7. CTA / CONTACT: Converging Nexus Particle Vortex
     ========================================================================== */
  function initCtaVisualizer() {
    createManagedVisualizer('cta-three-canvas', 'cta-three-container', function(scene, camera, isMobile, prefersReducedMotion) {
      camera.position.set(0, 0, 40);

      const group = new THREE.Group();
      scene.add(group);

      const vortexCount = isMobile ? 60 : 120;
      const vortexGeo = new THREE.BufferGeometry();
      const pos = new Float32Array(vortexCount * 3);
      const angles = [];

      for (let i = 0; i < vortexCount; i++) {
        const radius = Math.random() * 20 + 5;
        const angle = Math.random() * Math.PI * 2;
        angles.push({ r: radius, theta: angle, speed: 0.005 + (1 / radius) * 0.03 });

        pos[i * 3] = Math.cos(angle) * radius;
        pos[i * 3 + 1] = Math.sin(angle) * radius;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 10;
      }

      vortexGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const vortexMat = new THREE.PointsMaterial({ color: 0x6366f1, size: 2.4, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending });
      const vortexPoints = new THREE.Points(vortexGeo, vortexMat);
      group.add(vortexPoints);

      return {
        update: function(time, targetX, targetY) {
          group.rotation.y = targetX * 0.6;
          group.rotation.x = -targetY * 0.4;

          if (!prefersReducedMotion) {
            const arr = vortexGeo.attributes.position.array;
            for (let i = 0; i < vortexCount; i++) {
              angles[i].theta += angles[i].speed;
              arr[i * 3] = Math.cos(angles[i].theta) * angles[i].r;
              arr[i * 3 + 1] = Math.sin(angles[i].theta) * angles[i].r;
            }
            vortexGeo.attributes.position.needsUpdate = true;
          }
        }
      };
    });
  }

  // Master Initializer
  function initAllVisualizers() {
    initHeroVisualizer();
    initServicesVisualizer();
    initStagesVisualizer();
    initWorkVisualizer();
    initTechVisualizer();
    initPerfVisualizer();
    initCtaVisualizer();
  }

  window.GinaThreeHero = { init: initAllVisualizers };
})();
